(() => {
  const STOCKHOLM_TIME_ZONE = 'Europe/Stockholm';
  const pad = value => String(value).padStart(2, '0');
  const stockholmFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: STOCKHOLM_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  });

  const stockholmParts = timestamp => Object.fromEntries(
    stockholmFormatter.formatToParts(new Date(timestamp))
      .filter(part => part.type !== 'literal')
      .map(part => [part.type, Number(part.value)])
  );

  const getSwedishTarget = dataset => {
    const desired = {
      year: Number(dataset.liveYear),
      month: Number(dataset.liveMonth),
      day: Number(dataset.liveDay),
      hour: Number(dataset.liveHour),
      minute: Number(dataset.liveMinute),
      second: Number(dataset.liveSecond || 0)
    };
    if (Object.values(desired).some(value => !Number.isInteger(value))) return NaN;

    const wallTime = Date.UTC(desired.year, desired.month - 1, desired.day, desired.hour, desired.minute, desired.second);
    const calendarCheck = new Date(wallTime);
    if (
      calendarCheck.getUTCFullYear() !== desired.year
      || calendarCheck.getUTCMonth() + 1 !== desired.month
      || calendarCheck.getUTCDate() !== desired.day
      || calendarCheck.getUTCHours() !== desired.hour
      || calendarCheck.getUTCMinutes() !== desired.minute
    ) return NaN;

    let target = wallTime;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      const actual = stockholmParts(target);
      const actualWallTime = Date.UTC(actual.year, actual.month - 1, actual.day, actual.hour, actual.minute, actual.second);
      target += wallTime - actualWallTime;
    }

    const resolved = stockholmParts(target);
    const matches = ['year', 'month', 'day', 'hour', 'minute', 'second']
      .every(part => resolved[part] === desired[part]);
    return matches ? target : NaN;
  };

  const formatCountdown = milliseconds => {
    const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return days > 0
      ? `${days}d ${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`
      : `${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
  };

  const init = root => {
    if (!root || root.dataset.tiktokLiveInitialized === 'true') return;
    root.dataset.tiktokLiveInitialized = 'true';

    const message = root.querySelector('[data-tiktok-live-message]');
    const timer = root.querySelector('[data-tiktok-live-timer]');
    const countdownText = root.dataset.countdownText || 'Live on TikTok in';
    const activeText = root.dataset.activeText || "We're live on TikTok";
    const targetTime = getSwedishTarget(root.dataset);

    const showLiveState = () => {
      root.classList.add('is-live');
      if (message) message.textContent = activeText;
      if (timer) timer.hidden = true;
      root.setAttribute('aria-label', activeText);
    };

    if (!Number.isFinite(targetTime)) {
      showLiveState();
      return;
    }
    root.dataset.liveTargetUtc = new Date(targetTime).toISOString();

    const update = () => {
      const remaining = targetTime - Date.now();
      if (remaining <= 0) {
        showLiveState();
        window.clearInterval(root.tiktokLiveInterval);
        return;
      }

      const countdown = formatCountdown(remaining);
      root.classList.remove('is-live');
      if (message) message.textContent = countdownText;
      if (timer) {
        timer.hidden = false;
        timer.textContent = countdown;
      }
      root.setAttribute('aria-label', `${countdownText} ${countdown}`);
    };

    update();
    root.tiktokLiveInterval = window.setInterval(update, 1000);
  };

  const initAll = scope => scope.querySelectorAll?.('[data-tiktok-live]').forEach(init);
  const boot = () => initAll(document);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  window.addEventListener('pageshow', boot);
})();
